import React, {
  createContext,
  useEffect,
  useContext,
  useReducer,
  useRef,
} from "react";
import { initialState, searchReducer } from "./Reducers";
import DOMPurify from "dompurify";
import {
  getActiveFilters,
  getSanitizedParam,
  setFilterState,
} from "../../helpers";
import {
  getSuggestionHandler,
  postSearchHandler,
  postSearchHandler_search,
  postSearchHandler_customerSuccess,
} from "../Api";
import { throttle } from "lodash";
import {
  PF_SORT_ASCENDING,
  PF_SORT_DESCENDING,
  PF_SORT_FEATURED,
} from "../../constants/product-finder";
import {
  SEARCH_SORT_POPULAR,
  SEARCH_SORT_RELEVANCE,
} from "../../constants/site-search";
import { getSearchSortIndex } from "../../helpers/site-search";
import { getPFSortIndex } from "../../helpers/product_finder";

export const SearchContext = createContext();

export const SearchContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(searchReducer, initialState);

  // state
  const {
    componentElement,
    searchTerm,
    completeSearchResultSet,
    searchResultsFromQuery,
    selectedFilters,
    sortTypeApplied,
    filteredResults,
    mostPopularPagesArray,
    externalQueryId
  } = state;

  const passedInitialRender = useRef(false);

  const isSiteSearch = componentElement.getAttribute("id") === "site-search";
  const isCustomerSuccess =
    componentElement.getAttribute("id") === "customer-success-story";

  const handleResize = throttle(() => {
    // if the window width is greater than 1199px, close the filter drawer
    if (window.innerWidth > 1199) {
      dispatch({ type: "UPDATE_FILTER_DRAWER_IS_OPEN", payload: false });
    }
  }, 300); // Adjust throttle interval as needed

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // do initial search if there are search or filter query parameters
  useEffect(() => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop),
    });

    const filterParams = params?.filters || "";
    const sanitizedFiltersParam = DOMPurify.sanitize(decodeURI(filterParams));
    const sanitizedFiltersParamArray = sanitizedFiltersParam.split("&");

    sanitizedFiltersParamArray.forEach((filterId) => {
      setFilterState(filterId, componentElement, true);
    });

    dispatch({
      type: "UPDATE_SELECTED_FILTERS",
      payload: getActiveFilters(componentElement),
    });
    dispatch({ type: "SET_INITIAL_RENDER_REQUISITE", payload: false });

    const sanitizedParam = DOMPurify.sanitize(decodeURI(params.search));
    if (
      (sanitizedParam !== "null" && sanitizedParam !== searchTerm) ||
      filterParams !== ""
    ) {
      let searchParam = sanitizedParam;
      if (searchParam === "null") {
        searchParam = "*";
      } else {
        dispatch({ type: "UPDATE_SEARCH_TERM", payload: searchParam });
      }
      getSuggestionHandler(searchParam, dispatch);
      if (isSiteSearch) {
        postSearchHandler_search(searchParam, dispatch);
      } else if (isCustomerSuccess) {
        postSearchHandler_customerSuccess(searchParam, dispatch);
      } else {
        postSearchHandler(searchParam, dispatch);
      }
    } else if (isSiteSearch) {
      postSearchHandler_search("*", dispatch);
    } else if (isCustomerSuccess) {
      postSearchHandler_customerSuccess("*", dispatch);
    }
  }, []);

  useEffect(() => {
    if (selectedFilters && selectedFilters.length > 0) {
      dispatch({ type: "UPDATE_SHOW_RESULTS", payload: true });
    }
    const sortParam = getSanitizedParam("sort");
    const sortIndex = isSiteSearch
      ? getSearchSortIndex(sortParam)
      : getPFSortIndex(sortParam);
    sortResults(filterResults(), sortParam);
    dispatch({ type: "UPDATE_SORT_INDEX", payload: sortIndex });
  }, [completeSearchResultSet, searchResultsFromQuery, selectedFilters]);

  // Do initial search to get all results
  useEffect(() => {
    // if (!isSiteSearch) {
    //   postSearchHandler("*", dispatch, true);
    // }
    if (isCustomerSuccess) {
      postSearchHandler_customerSuccess("*", dispatch, true);
    } else if (!isSiteSearch) {
      postSearchHandler("*", dispatch, true);
    }
    dispatch({ type: "SET_INITIAL_RENDER_REQUISITE", payload: false });
  }, []);

  useEffect(() => {
    if (sortTypeApplied !== "") sortResults(filteredResults, sortTypeApplied);
  }, [sortTypeApplied]);

  useEffect(() => {
    if (
      passedInitialRender.current &&
      externalQueryId // Only push if externalQueryId is set
    ) {
      const pageName = isSiteSearch ? "Search" : "Product Finder";
      const pageType = isSiteSearch ? "Site Search" : "Product Finder";
      const wasFiltered = selectedFilters.length > 0 ? 1 : 0;
      const event =
        searchResultsFromQuery && searchResultsFromQuery.length > 0
          ? window.adobeDataLayerEvents?.component.searchSuccess
          : window.adobeDataLayerEvents?.component.searchNullResult;
      const siteSectionLevels = window.location.pathname
        .replaceAll(".html", "")
        .substring(1)
        .split("/");
      
      window.adobeDataLayer?.push({
        event: event,
        type: "Page Interaction",
        lumenPage: {
          pageName: pageName,
          pageOwner: "",
          siteSectionLevels: siteSectionLevels,
          pageCategory: "",
        },
        searchDetails: {
          externalQueryId,
          searchTerm: searchTerm,
          numberOfResults: filterResults(searchResultsFromQuery).length,
          filteredSearch: wasFiltered,
          type: pageType,
          pageDepth: 1,
        },
      });
    }
    passedInitialRender.current = true;
  }, [searchResultsFromQuery, externalQueryId]);

  // Filter the results according to which filters are selected
  const filterResults = (initialResults) => {
    let results;
    let filteredResultsArray = [];
    if (initialResults) {
      results = initialResults;
    } else if (!searchTerm || searchTerm === "" || searchTerm === "null") {
      results = completeSearchResultSet;
    } else {
      results = searchResultsFromQuery;
    }
    if (selectedFilters.length > 0) {
      results?.map((result) => {
        if (resultShouldBeIncluded(result.Fields)) {
          filteredResultsArray.push(result);
        }
      });
      return filteredResultsArray;
    }

    // else {
    return results;
    // }
    // return results;
  };

  const sortResultsByTitleAsc = (results) => {
    results.sort((a, b) => {
      return a.Fields.pageTitle_product.Value.localeCompare(
        b.Fields.pageTitle_product.Value
      );
    });
  };

  const sortResultsByTitleDesc = (results) => {
    results.sort((a, b) => {
      return b.Fields.pageTitle_product.Value.localeCompare(
        a.Fields.pageTitle_product.Value
      );
    });
  };

  const sortResultsByFeatured = (results) => {
    results.sort((a, b) => {
      return a.ResultIndex - b.ResultIndex;
    });
  };

  const comparePopularity = (a, b) => {
    const aIndex = mostPopularPagesArray.indexOf(a.Fields?.pagePath?.Value);
    const bIndex = mostPopularPagesArray.indexOf(b.Fields?.pagePath?.Value);
    if (aIndex < 0 && bIndex < 0) return 0;
    if (aIndex >= 0 && bIndex < 0) return -1;
    if (aIndex < 0 && bIndex >= 0) return 1;
    return aIndex - bIndex;
  };

  const sortResultsByRelevance = (results) => {
    results.sort((a, b) => {
      if (a.Score !== b.Score) return b.Score - a.Score;
      const popularity = comparePopularity(a, b);
      if (popularity !== 0) return popularity;
      return a.Fields.pageTitle_product.Value.localeCompare(
        b.Fields.pageTitle_product.Value
      );
    });
  };

  const sortResultsByPopularity = (results) => {
    results.sort((a, b) => {
      const popularity = comparePopularity(a, b);
      if (popularity !== 0) return popularity;
      if (a.Score !== b.Score) return b.Score - a.Score;
      return a.Fields.pageTitle_product.Value.localeCompare(
        b.Fields.pageTitle_product.Value
      );
    });
  };

  const sortResults = (results, type) => {
    const url = new URL(window.location.href);
    if (type !== "") url.searchParams.set("sort", type);
    if (type === PF_SORT_ASCENDING) sortResultsByTitleAsc(results);
    else if (type === PF_SORT_DESCENDING) sortResultsByTitleDesc(results);
    else if (type === SEARCH_SORT_POPULAR) sortResultsByPopularity(results);
    else if (isSiteSearch) {
      url.searchParams.set("sort", SEARCH_SORT_RELEVANCE);
      sortResultsByRelevance(results);
    } else {
      url.searchParams.set("sort", PF_SORT_FEATURED);
      sortResultsByFeatured(results);
    }
    // Only pushState if the URL is different
    if (window.location.href !== url.href) {
        window.history.pushState({}, "", url.href);
    }
    dispatch({ type: "UPDATE_SORTED_RESULTS", payload: results });
  };

  const resultShouldBeIncluded = (fields) => {
    if (selectedFilters.length <= 0) {
      return true;
    }

    let tagString = fields?.formattedPageTags?.Value || "[]";
    let tagArray = JSON.parse(tagString);

    let include = false;
    if (tagArray && tagArray.length > 0) {
      let itemTags = tagArray.map((tag) => {
        return tag.tagId;
      });
      include = selectedFilters.every((value) =>
        itemTags.includes(value.tagId)
      );
    }
    return include;
  };

  return (
    <SearchContext.Provider value={{ state, dispatch }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);

  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }

  return context;
};
